// pages/JavaScript.js
// Bài giảng JavaScript - Thiết kế Web tĩnh

import React from "react";

const styles = {
  page: {
    maxWidth: 900,
    margin: "0 auto",
    padding: "24px 20px 64px",
    color: "#1f2937",
    lineHeight: 1.65,
    fontSize: 16,
  },
  pageTitle: { fontSize: 30, fontWeight: 800, margin: "0 0 4px", color: "#111827" },
  pageSub: { color: "#6b7280", margin: "0 0 24px", fontSize: 15 },
  toc: {
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "16px 20px",
    margin: "0 0 32px",
  },
  tocTitle: { fontWeight: 700, margin: "0 0 8px" },
  tocLink: { color: "#2563eb", textDecoration: "none" },
  lessonTitle: {
    fontSize: 24,
    fontWeight: 800,
    margin: "40px 0 6px",
    color: "#b45309",
    borderBottom: "2px solid #fde68a",
    paddingBottom: 6,
  },
  meta: { color: "#6b7280", fontSize: 14, margin: "0 0 12px" },
  goalBox: {
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 10,
    padding: "12px 18px",
    margin: "0 0 24px",
  },
  h3: { fontSize: 19, fontWeight: 700, margin: "28px 0 8px", color: "#111827" },
  pre: {
    background: "#0f172a",
    color: "#e2e8f0",
    borderRadius: 10,
    padding: "14px 16px",
    overflowX: "auto",
    fontSize: 14,
    lineHeight: 1.5,
    margin: "10px 0 18px",
  },
  code: {
    background: "#f1f5f9",
    padding: "1px 6px",
    borderRadius: 4,
    fontSize: 14,
    color: "#be123c",
  },
  table: { borderCollapse: "collapse", width: "100%", margin: "10px 0 18px", fontSize: 15 },
  th: { border: "1px solid #d1d5db", padding: "8px 10px", background: "#f3f4f6", textAlign: "left" },
  td: { border: "1px solid #d1d5db", padding: "8px 10px" },
  note: {
    background: "#fefce8",
    border: "1px solid #fde68a",
    borderRadius: 8,
    padding: "10px 14px",
    margin: "14px 0",
    fontSize: 15,
  },
};

const Code = ({ children }) => <pre style={styles.pre}><code>{children}</code></pre>;

const practiceLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "#ecfdf5",
  border: "1px solid #a7f3d0",
  color: "#047857",
  textDecoration: "none",
  borderRadius: 8,
  padding: "6px 12px",
  fontSize: 14,
  fontWeight: 600,
  margin: "0 0 18px",
};
const Practice = ({ href, label }) => (
  <div>
    <a style={practiceLinkStyle} href={href} target="_blank" rel="noopener noreferrer">
      ▶ Thực hành tại W3Schools{label ? `: ${label}` : ""}
    </a>
  </div>
);
const Goal = ({ items }) => (
  <div style={styles.goalBox}>
    <strong>Mục tiêu của bài:</strong>
    <ul style={{ margin: "8px 0 0", paddingLeft: 22 }}>
      {items.map((t, i) => (
        <li key={i}>{t}</li>
      ))}
    </ul>
  </div>
);

const JavaScriptPage = () => (
  <div style={styles.page}>
    <h1 style={styles.pageTitle}>JavaScript — Lập trình cho trang Web</h1>
    <p style={styles.pageSub}>
      Bài giảng JavaScript: thêm tương tác và xử lý động cho trang HTML.
    </p>

    <Practice href="https://www.w3schools.com/js/" label="JavaScript Tutorial (trang chủ)" />
    <Practice href="https://www.w3schools.com/js/tryit.asp?filename=tryjs_default" label="Try it Yourself (viết & chạy JS ngay)" />

    <nav style={styles.toc}>
      <p style={styles.tocTitle}>Nội dung</p>
      <ul style={{ margin: 0, paddingLeft: 22 }}>
        <li><a style={styles.tocLink} href="#j1">1. JavaScript là gì & cách nhúng</a></li>
        <li><a style={styles.tocLink} href="#j2">2. Biến & kiểu dữ liệu</a></li>
        <li><a style={styles.tocLink} href="#j3">3. Toán tử</a></li>
        <li><a style={styles.tocLink} href="#j4">4. Rẽ nhánh (if / switch)</a></li>
        <li><a style={styles.tocLink} href="#j5">5. Vòng lặp (for / while)</a></li>
        <li><a style={styles.tocLink} href="#j6">6. Hàm (Functions)</a></li>
        <li><a style={styles.tocLink} href="#j7">7. Mảng & Đối tượng</a></li>
        <li><a style={styles.tocLink} href="#j8">8. Thao tác với HTML (DOM)</a></li>
        <li><a style={styles.tocLink} href="#j9">9. Sự kiện (Events)</a></li>
        <li><a style={styles.tocLink} href="#j10">10. Kiểm tra dữ liệu Form</a></li>
      </ul>
    </nav>

    <h2 style={styles.lessonTitle}>Bài giảng JavaScript</h2>
    <Goal
      items={[
        "Hiểu JavaScript là gì và cách nhúng vào trang HTML;",
        "Khai báo và sử dụng biến, kiểu dữ liệu, toán tử;",
        "Viết được các lệnh điều khiển: rẽ nhánh, vòng lặp, hàm;",
        "Làm việc với mảng và đối tượng;",
        "Thay đổi nội dung trang qua DOM và xử lý sự kiện;",
        "Kiểm tra (validate) dữ liệu nhập từ form.",
      ]}
    />

    <h3 id="j1" style={styles.h3}>1. JavaScript là gì & cách nhúng</h3>
    <p>
      <strong>JavaScript (JS)</strong> là ngôn ngữ lập trình chạy trong trình
      duyệt, giúp trang web có khả năng <strong>tương tác</strong>: phản hồi khi
      bấm nút, thay đổi nội dung, kiểm tra dữ liệu, gọi dữ liệu từ máy chủ… Bộ ba:
      HTML (nội dung) – CSS (giao diện) – JS (hành vi).
    </p>
    <Code>{`<!-- Nhúng trong thẻ <script> -->
<script>
  console.log("Xin chào từ JavaScript!");
  alert("Trang đã tải xong!");
</script>

<!-- Hoặc file ngoài (khuyên dùng) -->
<script src="main.js"></script>`}</Code>
    <div style={styles.note}>
      <strong>Mở Console:</strong> bấm <span style={styles.code}>F12</span> trong trình
      duyệt → tab <strong>Console</strong> để xem kết quả <span style={styles.code}>console.log</span> và lỗi.
    </div>
    <Practice href="https://www.w3schools.com/js/js_whereto.asp" label="JS Where To" />

    <h3 id="j2" style={styles.h3}>2. Biến & kiểu dữ liệu</h3>
    <Code>{`let ten = "An";        // chuỗi (string)
const tuoi = 20;       // số (number) - hằng số, không đổi
let dangHoc = true;    // luận lý (boolean)
let diem;              // chưa gán -> undefined

// let: thay đổi được; const: không đổi; (tránh dùng var)`}</Code>
    <p>Các kiểu cơ bản: <span style={styles.code}>string</span>, <span style={styles.code}>number</span>, <span style={styles.code}>boolean</span>, <span style={styles.code}>null</span>, <span style={styles.code}>undefined</span>, <span style={styles.code}>object</span>, <span style={styles.code}>array</span>.</p>
    <Practice href="https://www.w3schools.com/js/js_variables.asp" label="JS Variables" />

    <h3 id="j3" style={styles.h3}>3. Toán tử</h3>
    <Code>{`// Số học
let tong = 5 + 3;      // 8
let du = 10 % 3;       // 1 (chia lấy dư)

// So sánh (=== so sánh cả kiểu)
5 === "5"   // false
5 == "5"    // true  (nên dùng === để chắc chắn)

// Luận lý
true && false   // và
true || false   // hoặc
!true           // phủ định`}</Code>
    <Practice href="https://www.w3schools.com/js/js_operators.asp" label="JS Operators" />

    <h3 id="j4" style={styles.h3}>4. Rẽ nhánh (if / switch)</h3>
    <Code>{`let diem = 7;

if (diem >= 8) {
  console.log("Giỏi");
} else if (diem >= 5) {
  console.log("Đạt");
} else {
  console.log("Chưa đạt");
}

// switch
switch (thu) {
  case 7: console.log("Thứ Bảy"); break;
  default: console.log("Ngày thường");
}`}</Code>
    <Practice href="https://www.w3schools.com/js/js_if_else.asp" label="JS If...Else" />

    <h3 id="j5" style={styles.h3}>5. Vòng lặp (for / while)</h3>
    <Code>{`// for: biết trước số lần lặp
for (let i = 1; i <= 5; i++) {
  console.log("Lần " + i);
}

// while: lặp khi điều kiện còn đúng
let n = 3;
while (n > 0) {
  console.log(n);
  n--;
}`}</Code>
    <Practice href="https://www.w3schools.com/js/js_loop_for.asp" label="JS For Loop" />

    <h3 id="j6" style={styles.h3}>6. Hàm (Functions)</h3>
    <Code>{`// Khai báo hàm
function cong(a, b) {
  return a + b;
}
console.log(cong(2, 3)); // 5

// Hàm mũi tên (arrow function)
const nhan = (a, b) => a * b;
console.log(nhan(2, 4)); // 8`}</Code>
    <Practice href="https://www.w3schools.com/js/js_functions.asp" label="JS Functions" />

    <h3 id="j7" style={styles.h3}>7. Mảng & Đối tượng</h3>
    <Code>{`// Mảng (array)
let mon = ["Toán", "Lý", "Hóa"];
console.log(mon[0]);     // "Toán"
mon.push("Sinh");        // thêm phần tử
console.log(mon.length); // 4

// Đối tượng (object)
let sinhVien = {
  ten: "An",
  tuoi: 20,
  chao: function () { return "Chào " + this.ten; }
};
console.log(sinhVien.ten);     // "An"
console.log(sinhVien.chao());  // "Chào An"`}</Code>
    <Practice href="https://www.w3schools.com/js/js_arrays.asp" label="JS Arrays" />

    <h3 id="j8" style={styles.h3}>8. Thao tác với HTML (DOM)</h3>
    <p>
      <strong>DOM</strong> cho phép JS đọc và thay đổi nội dung trang. Lấy phần tử
      rồi đổi nội dung, kiểu dáng:
    </p>
    <Code>{`<p id="kq">Chưa có gì</p>
<script>
  // Lấy phần tử theo id
  let p = document.getElementById("kq");
  p.innerText = "Đã thay đổi nội dung!";  // đổi chữ
  p.style.color = "red";                  // đổi màu

  // Lấy theo bộ chọn CSS
  document.querySelector(".tieu-de").textContent = "Tiêu đề mới";
</script>`}</Code>
    <Practice href="https://www.w3schools.com/js/js_htmldom.asp" label="JS HTML DOM" />

    <h3 id="j9" style={styles.h3}>9. Sự kiện (Events)</h3>
    <Code>{`<button id="btn">Bấm tôi</button>
<script>
  // Cách 1: thuộc tính onclick
  // <button onclick="alert('Hi')">...</button>

  // Cách 2: addEventListener (khuyên dùng)
  document.getElementById("btn").addEventListener("click", function () {
    alert("Bạn vừa bấm nút!");
  });
</script>`}</Code>
    <p>Một số sự kiện hay dùng: <span style={styles.code}>click</span>, <span style={styles.code}>mouseover</span>, <span style={styles.code}>keydown</span>, <span style={styles.code}>change</span>, <span style={styles.code}>submit</span>.</p>
    <Practice href="https://www.w3schools.com/js/js_events.asp" label="JS Events" />

    <h3 id="j10" style={styles.h3}>10. Kiểm tra dữ liệu Form</h3>
    <Code>{`<form onsubmit="return kiemTra()">
  <input type="text" id="ten" placeholder="Họ tên" />
  <button type="submit">Gửi</button>
</form>
<script>
  function kiemTra() {
    let ten = document.getElementById("ten").value;
    if (ten.trim() === "") {
      alert("Vui lòng nhập họ tên!");
      return false;   // chặn gửi form
    }
    return true;      // cho phép gửi
  }
</script>`}</Code>
    <Practice href="https://www.w3schools.com/js/js_validation.asp" label="JS Form Validation" />

    <div style={{ ...styles.note, marginTop: 32 }}>
      <strong>Thực hành:</strong> mở "Try it Yourself", dán từng đoạn code và bấm
      "Run" để xem kết quả. Nhớ mở Console (F12) để theo dõi <span style={styles.code}>console.log</span> và báo lỗi.
    </div>
  </div>
);

export default JavaScriptPage;
